import React from 'react';
import {
  BookOpen,
  HeartHandshake,
  Wallet,
  LineChart,
  HeartPulse,
  Brain,
  Trees,
  Mail,
  Library,
  ExternalLink,
  Sparkles,
} from 'lucide-react';
import './Articles.css';

const articleCategories = [
  {
    icon: <HeartHandshake className="article-card-icon" />,
    title: '陪伴孩子成長',
    description:
      '陪伴不是控制，也不是急著給答案，而是在孩子成長的路上，給予理解、耐心與信任。',
  },
  {
    icon: <Wallet className="article-card-icon" />,
    title: '安心理財',
    description:
      '理財不只是追求報酬，更是學習讓自己在金錢面前，保持清明、穩定與長期的眼光。',
  },
  {
    icon: <LineChart className="article-card-icon" />,
    title: '不看盤人生',
    description:
      '把時間還給生活，不讓每天的漲跌綁架情緒，學習用更長的時間尺度來看待人生與投資。',
  },
  {
    icon: <HeartPulse className="article-card-icon" />,
    title: '健康與身心靈',
    description:
      '身體、情緒、精神與生活節奏彼此相連，真正的穩定，來自內外一致的照顧與修整。',
  },
  {
    icon: <Brain className="article-card-icon" />,
    title: '第一性原理',
    description:
      '回到問題最根本的地方，重新拆解、重新理解，讓思考不只是記憶答案，而是看見本質。',
  },
  {
    icon: <Trees className="article-card-icon" />,
    title: '森林學校理念',
    description:
      '森林學校不追逐表面的成功，而是希望陪伴人回到生命根本，成為根深、安定而有力量的人。',
  },
  {
    icon: <Mail className="article-card-icon" />,
    title: '家書 / 人生價值 / 生命學習',
    description:
      '關於挫折、陪伴、家庭、責任與成長，這些文字不是說教，而是生命走過之後的分享與提醒。',
  },
  {
    icon: <Library className="article-card-icon" />,
    title: '延伸閱讀（活動前可先看看）',
    description:
      '在參加活動之前，先透過文字慢慢走近這些理念，會讓你在活動中有更深的感受與連結。',
  },
];

const recommendedBooks = [
  {
    title: '《一張圖，陪伴一生》',
    platform: 'Kobo',
    url: 'https://www.kobo.com/tw/zh/search?query=%E4%B8%80%E5%BC%B5%E5%9C%96%E9%99%AA%E4%BC%B4%E4%B8%80%E7%94%9F',
    description:
      '從人生、成長、陪伴與時間的角度，帶你重新理解「根深」與「繁茂」的關係。',
  },
  {
    title: '《安心理財》',
    platform: 'Amazon',
    url: 'https://www.amazon.com/Chinese-%E6%B4%AA%E5%85%A8%E9%93%AD-ebook/dp/B07YTS26PQ',
    description:
      '不是教你焦慮追價，而是幫助你建立長期、穩定、能安心生活的理財觀。',
  },
];

const Articles: React.FC = () => {
  return (
    <div className="articles-page">
      {/* Hero */}
      <section className="articles-hero">
        <div className="articles-hero-overlay"></div>
        <div className="articles-hero-content">
          <span className="articles-badge">Articles</span>
          <h1>文章 / 分享</h1>
          <p>
            有些學習不是在課堂上完成，
            <br />
            而是在閱讀、思考與生活中慢慢沉澱。
            <br />
            這裡收集的是一些可以陪伴你走一段路的文字。
          </p>
        </div>
      </section>

      {/* Intro */}
      <section className="articles-section section-width">
        <div className="articles-section-header">
          <Sparkles className="articles-section-icon" />
          <h2>文章方向</h2>
        </div>
        <p className="articles-intro-text">
          森林學校的文章，不只是知識整理，
          更希望成為一種陪伴。
          有些文字幫助你思考，有些文字幫助你安定，
          有些文字只是提醒你：人生不必急，根深之後，繁茂自來。
        </p>
      </section>

      {/* Categories */}
      <section className="articles-section section-width">
        <div className="articles-grid">
          {articleCategories.map((item, index) => (
            <article className="article-card" key={index}>
              <div className="article-card-top">
                {item.icon}
                <h3>{item.title}</h3>
              </div>
              <p>{item.description}</p>
            </article>
          ))}
        </div>
      </section>

      {/* Suggested layout for future articles */}
      <section className="articles-section section-width">
        <div className="articles-section-header">
          <BookOpen className="articles-section-icon" />
          <h2>可持續新增的文章專區</h2>
        </div>

        <div className="article-layout-card">
          <div className="article-layout-block">
            <h3>最新文章</h3>
            <p>可放近期新增的文章摘要與連結，方便讀者快速瀏覽。</p>
          </div>

          <div className="article-layout-block">
            <h3>主題分類</h3>
            <p>可依照陪伴、理財、健康、思考、人生等主題進行整理。</p>
          </div>

          <div className="article-layout-block">
            <h3>活動前閱讀</h3>
            <p>活動報名前或參加前，先閱讀這些文章，會更容易理解森林學校的理念。</p>
          </div>
        </div>
      </section>

      {/* Recommended Reading */}
      <section className="articles-section section-width">
        <div className="articles-section-header">
          <Library className="articles-section-icon" />
          <h2>延伸閱讀</h2>
        </div>

        <div className="reading-grid">
          {recommendedBooks.map((book, index) => (
            <div className="reading-card" key={index}>
              <div className="reading-card-header">
                <div>
                  <h3>{book.title}</h3>
                  <span className="reading-platform">{book.platform}</span>
                </div>
              </div>

              <p className="reading-description">{book.description}</p>

              <a
                href={book.url}
                target="_blank"
                rel="noopener noreferrer"
                className="reading-link"
              >
                前往閱讀 <ExternalLink className="reading-link-icon" />
              </a>
            </div>
          ))}
        </div>
      </section>

      {/* Closing */}
      <section className="articles-section section-width articles-bottom-space">
        <div className="articles-closing-card">
          <h2>閱讀，不是為了知道更多</h2>
          <p>
            而是為了讓心更安定，讓方向更清楚，
            <br />
            讓你在真正重要的人生課題上，慢慢長出自己的答案。
          </p>
        </div>
      </section>
    </div>
  );
};

export default Articles;